/**
 * The slot the one map lives in while this component is mounted.
 *
 * It owns no map. It hands its own node to `mapHost`, which moves the single
 * container into it, and returns it to the parking lot on unmount — which is
 * how the same WebGL canvas travels from the board's side card to the whole
 * screen in the navigation mode without being rebuilt (design §1.4).
 *
 * StrictMode runs this effect twice in development: `attach → park → attach`.
 * That is safe precisely because nothing here CONSTRUCTS anything — the map is
 * a memoised module-level promise, so the second pass gets the first map.
 */

import { useEffect, useRef } from 'react';

import { cn } from '../lib/cn';
import { acquireMap, attachMap, parkMap, type MapInit } from './mapHost';

export default function MapViewport({
  init,
  className,
}: {
  init: MapInit;
  className?: string;
}) {
  const slotRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    const node = slotRef.current;
    if (!node) return;
    void acquireMap(init).then((handle) => {
      // The card may have left the board while the SDK was still loading.
      if (!live || !handle) return;
      attachMap(node);
    });
    return () => {
      live = false;
      // The node is parked only if it is still IN this slot. During a card swap
      // both viewports are mounted for the length of the removed card's exit
      // animation, and the new one has usually taken it already — see
      // `mapHost.parkMap`.
      parkMap(node);
    };
    // The first mount's `init` builds the map; later ones only reparent, and
    // the caller frames the map with `fitBounds` anyway.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return <div ref={slotRef} className={cn('mapgl-slot', className)} />;
}
